import { useState, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { GlobalStyles } from "@mui/material";
import VuiBox from "components/ui/VuiBox";
import VuiTypography from "components/ui/VuiTypography";
import VuiInput from "components/ui/VuiInput";
import VuiButton from "components/ui/VuiButton";
import VuiAlert from "components/ui/VuiAlert";
import GradientBorder from "components/GradientBorder";
import PageLayout from "components/layout/PageLayout";
import { GRADIENTS } from "utils/designTokens";
import porteriqLogo from "assets/images/logos/porteriq-logo-insights.png";
import { authService } from "services";

function ResetPassword() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [token, setToken] = useState("");
  const [status, setStatus] = useState({ type: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [cardLoaded, setCardLoaded] = useState(false);

  const query = new URLSearchParams(useLocation().search);
  const history = useHistory();

  useEffect(() => {
    const timer = setTimeout(() => setCardLoaded(true), 120);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const tokenParam = query.get("token");
    if (tokenParam) {
      setToken(tokenParam);
    } else {
      setStatus({ type: "error", message: "Invalid or missing reset token. Please request a new link." });
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setStatus({ type: "error", message: "Passwords do not match." });
      return;
    }
    setLoading(true);
    setStatus({ type: "", message: "" });
    try {
      await authService.resetPassword(token, password);
      setStatus({ type: "success", message: "Password reset successfully! Redirecting to sign in..." });
      setTimeout(() => history.push("/authentication/sign-in"), 2000);
    } catch (error) {
      setStatus({
        type: "error",
        message: error.response?.data?.message || "Failed to reset password. The link may have expired.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout background={GRADIENTS.pageBg}>
      <GlobalStyles
        styles={{
          "@keyframes blobDrift1": {
            "0%, 100%": { transform: "translate(0px, 0px) scale(1)" },
            "33%": { transform: "translate(50px, -40px) scale(1.1)" },
            "66%": { transform: "translate(-28px, 24px) scale(0.94)" },
          },
          "@keyframes blobDrift2": {
            "0%, 100%": { transform: "translate(0px, 0px) scale(1)" },
            "50%": { transform: "translate(-58px, 30px) scale(1.08)" },
          },
        }}
      />

      {/* Blue blob — top left */}
      <VuiBox
        sx={{
          position: "absolute", top: "-240px", left: "-200px",
          width: "660px", height: "660px",
          background: "radial-gradient(circle, rgba(0,117,255,0.34) 0%, transparent 68%)",
          filter: "blur(108px)",
          animation: "blobDrift1 22s ease-in-out infinite",
          borderRadius: "62% 38% 72% 28% / 52% 64% 36% 48%",
          zIndex: 0, pointerEvents: "none",
        }}
      />
      {/* Purple blob — bottom right */}
      <VuiBox
        sx={{
          position: "absolute", bottom: "-180px", right: "-140px",
          width: "600px", height: "600px",
          background: "radial-gradient(circle, rgba(124,58,237,0.30) 0%, transparent 68%)",
          filter: "blur(118px)",
          animation: "blobDrift2 19s ease-in-out infinite",
          borderRadius: "42% 58% 48% 52% / 62% 38% 62% 38%",
          zIndex: 0, pointerEvents: "none",
        }}
      />

      {/* Centered card */}
      <VuiBox
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        px={2}
        position="relative"
        zIndex={2}
        sx={{ overflowY: { xs: "auto", md: "hidden" }, py: { xs: 4, md: 0 } }}
      >
        {/* Gradient border wrapper */}
        <VuiBox
          sx={{
            width: "100%",
            maxWidth: "440px",
            padding: "1.5px",
            borderRadius: "25.5px",
            background: "linear-gradient(135deg, rgba(0,117,255,0.65) 0%, rgba(124,58,237,0.45) 100%)",
            boxShadow: "0 28px 90px rgba(0,0,0,0.75), 0 0 130px rgba(0,117,255,0.12)",
            opacity: cardLoaded ? 1 : 0,
            transform: cardLoaded ? "translateY(0)" : "translateY(26px)",
            transition: "opacity 0.75s ease-out, transform 0.75s ease-out",
          }}
        >
          <VuiBox
            sx={{
              background: "rgba(6, 10, 20, 0.55)",
              backdropFilter: "blur(22px)",
              borderRadius: "24px",
              padding: { xs: "32px 24px", sm: "44px 40px" },
            }}
          >
            {/* Logo */}
            <VuiBox display="flex" flexDirection="column" alignItems="center" mb={3.5}>
              <a href="https://www.porteriq.com/" style={{ display: "block", marginBottom: "10px" }}>
                <VuiBox
                  component="img"
                  src={porteriqLogo}
                  alt="PorterIQ"
                  sx={{ height: "96px", objectFit: "contain" }}
                />
              </a>
              <VuiTypography
                sx={{
                  color: "#78A7FC",
                  fontSize: "0.6rem",
                  letterSpacing: "0.22em",
                  textTransform: "uppercase",
                  fontWeight: "700",
                  opacity: 0.9,
                }}
              >
                Competitive Intelligence Hub
              </VuiTypography>
            </VuiBox>

            {/* Heading */}
            <VuiBox textAlign="center" mb={3}>
              <VuiTypography variant="h4" color="white" fontWeight="bold" mb={0.75}>
                Reset Password
              </VuiTypography>
              <VuiTypography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.875rem" }}
              >
                Choose a new password for your account
              </VuiTypography>
            </VuiBox>

            {/* Form */}
            <VuiBox component="form" role="form" onSubmit={handleSubmit}>
              {status.message && (
                <VuiBox mb={2}>
                  <VuiAlert
                    color={status.type === "success" ? "success" : "error"}
                    sx={(theme) => ({
                      backgroundImage: "none",
                      backgroundColor: status.type === "success"
                        ? "rgba(34,197,94,0.15)"
                        : "rgba(90,160,230,0.25)",
                      border: `1px solid ${status.type === "success"
                        ? "rgba(34,197,94,0.35)"
                        : "rgba(90,160,230,0.45)"}`,
                      borderRadius: theme.borders.borderRadius.md,
                      padding: theme.spacing(1.25, 2),
                      minHeight: "auto",
                      boxShadow: "none",
                      "& > .MuiBox-root": {
                        color: `${theme.palette.common.white} !important`,
                        fontSize: theme.typography.pxToRem(13.5),
                        fontWeight: theme.typography.fontWeightRegular,
                        lineHeight: 1.45,
                        padding: theme.spacing(0.5, 0),
                      },
                    })}
                  >
                    {status.message}
                  </VuiAlert>
                </VuiBox>
              )}

              <VuiBox mb={2.5}>
                <VuiBox mb={0.5} ml={0.5}>
                  <VuiTypography component="label" variant="button" color="white" fontWeight="medium">
                    New Password
                  </VuiTypography>
                </VuiBox>
                <GradientBorder
                  minWidth="100%"
                  padding="1px"
                  borderRadius="10px"
                  backgroundImage={GRADIENTS.cardBorder}
                >
                  <VuiInput
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    autoComplete="new-password"
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    sx={({ typography: { size } }) => ({
                      fontSize: size.sm,
                      padding: "12px 16px",
                      borderRadius: "9px",
                      width: "100%",
                    })}
                  />
                </GradientBorder>
              </VuiBox>

              <VuiBox mb={3.5}>
                <VuiBox mb={0.5} ml={0.5}>
                  <VuiTypography component="label" variant="button" color="white" fontWeight="medium">
                    Confirm Password
                  </VuiTypography>
                </VuiBox>
                <GradientBorder
                  minWidth="100%"
                  padding="1px"
                  borderRadius="10px"
                  backgroundImage={GRADIENTS.cardBorder}
                >
                  <VuiInput
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    autoComplete="new-password"
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    sx={({ typography: { size } }) => ({
                      fontSize: size.sm,
                      padding: "12px 16px",
                      borderRadius: "9px",
                      width: "100%",
                    })}
                  />
                </GradientBorder>
              </VuiBox>

              <VuiBox mb={1}>
                <VuiButton
                  type="submit"
                  color="info"
                  fullWidth
                  disabled={loading || !token}
                  sx={{
                    backgroundColor: "#1A73E8",
                    color: "white",
                    padding: "12px 0",
                    fontSize: "0.875rem",
                    fontWeight: "600",
                    letterSpacing: "0.02em",
                    boxShadow: "0 4px 20px rgba(26,115,232,0.35)",
                    "&:hover": {
                      backgroundColor: "#1767D2",
                      boxShadow: "0 4px 28px rgba(26,115,232,0.5)",
                    },
                    "&:disabled": { opacity: 0.7 },
                  }}
                >
                  {loading ? "Resetting..." : "Reset Password"}
                </VuiButton>
              </VuiBox>
            </VuiBox>
          </VuiBox>
        </VuiBox>
      </VuiBox>
    </PageLayout>
  );
}

export default ResetPassword;
